import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const SoftwareLicensesGrid = ({ data, onUpdate, onDelete, searchQuery }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const licenseTypeOptions = [
    { value: 'monthly', label: 'Monthly Subscription' },
    { value: 'annual', label: 'Annual Subscription' },
    { value: 'perpetual', label: 'Perpetual License' },
    { value: 'onetime', label: 'One-Time Purchase' }
  ];

  const categoryOptions = [
    { value: 'design', label: 'Design Software' },
    { value: 'development', label: 'Development Tools' },
    { value: 'project', label: 'Project Management' },
    { value: 'productivity', label: 'Productivity Suite' },
    { value: 'security', label: 'Security Software' }
  ];

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery?.toLowerCase();
    return data?.filter(item => 
      item?.softwareName?.toLowerCase()?.includes(query) ||
      item?.vendor?.toLowerCase()?.includes(query) ||
      item?.category?.toLowerCase()?.includes(query)
    );
  }, [data, searchQuery]);

  const handleEdit = (item) => {
    setEditingId(item?.id);
    setEditData(item);
  };

  const handleSave = () => {
    onUpdate(editData);
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => 
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows?.length === filteredData?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData?.map(item => item?.id));
    }
  };

  const formatCurrency = (value) => {
    return `OMR ${parseFloat(value || 0)?.toFixed(3)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDaysUntilRenewal = (renewalDate) => {
    const today = new Date();
    const renewal = new Date(renewalDate);
    const diffTime = renewal - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[1300px]">
        <thead>
          <tr className="bg-muted border-b border-border">
            <th className="w-12 px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedRows?.length === filteredData?.length && filteredData?.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-border"
              />
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Software Name</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Vendor</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">License Type</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Cost</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Licenses</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Renewal Date</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Status</th>
            <th className="w-32 px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData?.map((item) => {
            const daysUntilRenewal = getDaysUntilRenewal(item?.renewalDate);
            const renewalStatus = daysUntilRenewal < 30 ? 'warning' : daysUntilRenewal < 90 ? 'info' : 'success';
            
            return (
              <tr key={item?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedRows?.includes(item?.id)}
                    onChange={() => handleSelectRow(item?.id)}
                    className="w-4 h-4 rounded border-border"
                  />
                </td>
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Input
                      type="text"
                      value={editData?.softwareName}
                      onChange={(e) => setEditData({...editData, softwareName: e?.target?.value})}
                      className="w-full"
                    />
                  ) : (
                    <span className="text-sm text-foreground font-medium">{item?.softwareName}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Input
                      type="text"
                      value={editData?.vendor}
                      onChange={(e) => setEditData({...editData, vendor: e?.target?.value})}
                      className="w-full"
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">{item?.vendor}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Select
                      options={categoryOptions}
                      value={editData?.category}
                      onChange={(value) => setEditData({...editData, category: value})}
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground capitalize">{item?.category}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Select
                      options={licenseTypeOptions}
                      value={editData?.licenseType}
                      onChange={(value) => setEditData({...editData, licenseType: value})}
                    />
                  ) : (
                    <span className="text-sm text-foreground capitalize">{item?.licenseType?.replace('_', ' ')}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {editingId === item?.id ? (
                    <Input
                      type="number"
                      value={editData?.cost}
                      onChange={(e) => setEditData({...editData, cost: parseFloat(e?.target?.value)})}
                      className="w-full text-right"
                    />
                  ) : (
                    <span className="text-sm text-foreground font-medium">{formatCurrency(item?.cost)}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {editingId === item?.id ? (
                    <Input
                      type="number"
                      value={editData?.numberOfLicenses}
                      onChange={(e) => setEditData({...editData, numberOfLicenses: parseInt(e?.target?.value)})}
                      className="w-full text-center"
                    />
                  ) : (
                    <span className="text-sm text-foreground">{item?.numberOfLicenses}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Input
                      type="date"
                      value={editData?.renewalDate}
                      onChange={(e) => setEditData({...editData, renewalDate: e?.target?.value})}
                      className="w-full"
                    />
                  ) : (
                    <span className="text-sm text-foreground">{formatDate(item?.renewalDate)}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {daysUntilRenewal < 0 ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-error/10 text-error">
                      Expired
                    </span>
                  ) : daysUntilRenewal < 30 ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-warning/10 text-warning">
                      {daysUntilRenewal}d left
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-success/10 text-success">
                      Active
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    {editingId === item?.id ? (
                      <>
                        <Button
                          variant="success"
                          size="xs"
                          iconName="Check"
                          onClick={handleSave}
                        />
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="X"
                          onClick={handleCancel}
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="Edit"
                          onClick={() => handleEdit(item)}
                        />
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="Trash2"
                          onClick={() => onDelete(item?.id)}
                        />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {filteredData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="FileX" size={48} className="text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">No software licenses found</p>
        </div>
      )}
    </div>
  );
};

export default SoftwareLicensesGrid;